import { type Stripe, type StripeElements } from "@stripe/stripe-js";
import { makeAutoObservable } from "mobx";

import { type AnalyticsManager } from "services/analytics/analyticsManager";
import { type BillingClient } from "services/billing/billingClient";
import { type CheckoutResponseData, PaymentStatus } from "services/billing/billingTypes";
import { type SetupIntentStore } from "services/setupIntent/setupIntentStore";

type ExpressCheckoutState = "idle" | "loading" | "ready" | "processing" | "success" | "error";

// confirmSetup resolves as soon as Stripe accepts the SetupIntent, but the
// backend can only see the payment method once Stripe's setup_intent.succeeded
// webhook has reached fees/stripe-integration. Retry the charge briefly to
// cover that propagation window before declaring failure.
const CHARGE_MAX_ATTEMPTS = 3;
const CHARGE_RETRY_DELAY_MS = 750;

export class ExpressCheckoutStore {
  private _state: ExpressCheckoutState = "idle";
  private _errorMessage: string | null = null;

  constructor(
    private readonly _setupIntentStore: SetupIntentStore,
    private readonly _billingClient: BillingClient,
    private readonly _analyticsManager: AnalyticsManager,
  ) {
    makeAutoObservable(this, {
      _setupIntentStore: false,
      _billingClient: false,
      _analyticsManager: false,
    } as any);
  }

  get state(): ExpressCheckoutState {
    return this._state;
  }

  private set state(value: ExpressCheckoutState) {
    this._state = value;
  }

  get stripe(): Stripe | null {
    return this._setupIntentStore.stripe;
  }

  get clientSecret(): string | null {
    return this._setupIntentStore.clientSecret;
  }

  get errorMessage(): string | null {
    return this._errorMessage;
  }

  async init(): Promise<void> {
    if (this._state !== "idle") {
      return;
    }

    this._analyticsManager.event("express-checkout.init");
    this.state = "loading";

    await this._setupIntentStore.init();

    if (this._setupIntentStore.state === "ready") {
      this.state = "ready";
      return;
    }

    this._analyticsManager.event("express-checkout.error", { error: "setup-intent-failed" });
    this.state = "error";
  }

  async confirmAndCharge(stripe: Stripe, elements: StripeElements): Promise<void> {
    if (this._state === "processing") {
      return;
    }

    this._analyticsManager.event("express-checkout.confirm");
    this.state = "processing";
    this._errorMessage = null;

    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (result.error) {
        this._analyticsManager.event("express-checkout.error", { error: result.error.message });
        this._errorMessage = result.error.message ?? "Payment setup failed. Please try again.";
        this.state = "error";
        return;
      }

      const chargeResult = await this._chargeWithRetry();

      if (!chargeResult.isPaid) {
        this._analyticsManager.event("express-checkout.error", { error: "charge-failed" });
        this._errorMessage = "Payment failed. Please try again or use a different payment method.";
        this.state = "error";
        return;
      }

      const paymentStatus = await this._billingClient.getPaymentStatus();

      if (paymentStatus.status === PaymentStatus.Paid) {
        this._analyticsManager.event("express-checkout.success");
        this.state = "success";
      } else {
        this._analyticsManager.event("express-checkout.error", { error: `status-${paymentStatus.status}` });
        this._errorMessage = "Payment could not be completed. Please try again.";
        this.state = "error";
      }
    } catch (e) {
      this._analyticsManager.event("express-checkout.error", { error: e instanceof Error ? e.message : String(e) });
      this._errorMessage = "Something went wrong. Please try again.";
      this.state = "error";
      console.error(e);
    }
  }

  private async _chargeWithRetry(): Promise<CheckoutResponseData> {
    let result = await this._billingClient.charge();
    for (let attempt = 2; attempt <= CHARGE_MAX_ATTEMPTS && !result.isPaid; attempt++) {
      this._analyticsManager.event("express-checkout.charge-retry", { attempt });
      await new Promise((resolve) => setTimeout(resolve, CHARGE_RETRY_DELAY_MS));
      result = await this._billingClient.charge();
    }
    return result;
  }
}
